import { bioEntityResponseSchema } from '@/models/bioEntityResponseSchema';
import { apiPath } from '@/redux/apiPath';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { BioEntityContent, BioEntityResponse } from '@/types/models';
import { PerfectSearchParams } from '@/types/search';
import { ThunkConfig } from '@/types/store';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getError } from '@/utils/error-report/getError';
import { BioEntitySearchedIdObject } from '@/redux/bioEntity/bioEntity.types';
import getModelElementsIdsFromReaction from '@/components/Map/MapViewer/utils/listeners/mouseClick/getModelElementsIdsFromReaction';
import getModelElementsElementIdsFromReaction from '@/redux/bioEntity/thunks/utils/getModelElementsElementIdsFromReaction';
import { BIO_ENTITY_FETCHING_ERROR_PREFIX } from '../bioEntity.constants';

type GetBioEntityProps = PerfectSearchParams;

export const getBioEntity = createAsyncThunk<
  | {
      content: BioEntityContent[];
      searchQuery: string;
      searchedReactions: BioEntitySearchedIdObject[];
      searchedModelElements: BioEntitySearchedIdObject[];
      modelElementsIds: number[];
    }
  | undefined,
  GetBioEntityProps,
  ThunkConfig
>('project/getBioEntityContents', async ({ searchQuery, isPerfectMatch }) => {
  try {
    const response = await axiosInstanceNewAPI.get<BioEntityResponse>(
      apiPath.getBioEntityContentsStringWithQuery({ searchQuery, isPerfectMatch }),
    );

    const isDataValidBioEnity = validateDataUsingZodSchema(response.data, bioEntityResponseSchema);

    if (!isDataValidBioEnity) {
      return undefined;
    }

    const searchedReactions: BioEntitySearchedIdObject[] = [];
    const searchedModelElements: BioEntitySearchedIdObject[] = [];
    const modelElementsIds: number[] = [];

    const bioEntityContents = response.data.content;
    bioEntityContents.forEach(bioEntityContent => {
      if ('products' in bioEntityContent.bioEntity) {
        searchedReactions.push({
          id: bioEntityContent.bioEntity.id,
          modelId: bioEntityContent.bioEntity.map,
          perfect: bioEntityContent.perfect,
          searchQuery,
        });
        const reactionModelElementsIds = getModelElementsIdsFromReaction(
          bioEntityContent.bioEntity,
        ).map(id => ({
          id,
          modelId: bioEntityContent.bioEntity.map,
          perfect: bioEntityContent.perfect,
          searchQuery,
        }));
        searchedModelElements.push(...reactionModelElementsIds);
        modelElementsIds.push(
          ...getModelElementsElementIdsFromReaction(bioEntityContent.bioEntity),
        );
      } else {
        modelElementsIds.push(bioEntityContent.bioEntity.id);
        searchedModelElements.push({
          id: bioEntityContent.bioEntity.id,
          modelId: bioEntityContent.bioEntity.map,
          perfect: bioEntityContent.perfect,
          searchQuery,
        });
      }
    });

    return {
      content: response.data.content,
      searchQuery,
      searchedReactions,
      searchedModelElements,
      modelElementsIds,
    };
  } catch (error) {
    return Promise.reject(getError({ error, prefix: BIO_ENTITY_FETCHING_ERROR_PREFIX }));
  }
});
